import { api } from '../api';
import type { QBlock, QContext } from './qClient';

// Q i fältet: personens position till Q:s sammanhang och en stillbild från kameran ("Titta").
// Bara på begäran – ingen kontinuerlig analys (kostnaden).

type Pos = NonNullable<QContext['position']>;

/** Enhetens position (webbläsaren/WKWebView). null om platstjänster är av eller det tar för lång tid. */
export function devicePosition(timeoutMs = 4000): Promise<Pos | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise(resolve => {
    const done = (p: Pos | null) => { clearTimeout(timer); resolve(p); };
    const timer = setTimeout(() => resolve(null), timeoutMs + 500);
    navigator.geolocation.getCurrentPosition(
      p => done({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, at: new Date(p.timestamp).toISOString() }),
      () => done(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}

/** Toppradens Q: bara positionen. */
export async function deviceContext(): Promise<QContext> {
  const position = await devicePosition();
  return position ? { position } : {};
}

export interface QLook { block: QBlock; preview: string; url?: string }

/** Ritar en bildruta ur ett kameraspår (telefonens kamera eller glasögonens ström) till en JPEG. */
export async function frameFromTrack(track: MediaStreamTrack, max = 1568): Promise<Blob> {
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = new MediaStream([track]);
  try {
    await video.play();
    if (!video.videoWidth) await new Promise<void>((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('Ingen bild från kameran')), 3000);
      video.onloadeddata = () => { clearTimeout(t); resolve(); };
    });
    const scale = Math.min(1, max / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext('2d')!.drawImage(video, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Bilden gick inte att spara'))), 'image/jpeg', 0.85));
  } finally {
    video.pause();
    video.srcObject = null;
  }
}

/** JPEG till ett bildblock för Claude och en förhandsvisning (data-URL). Exporterad för tester. */
export async function blobToLook(blob: Blob): Promise<QLook> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  const data = btoa(bin);
  return { block: { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data } } as QBlock, preview: `data:image/jpeg;base64,${data}` };
}

/** Stillbild från spåret, uppladdad så att den kan kopplas till fällt vilt (photoUrl). Uppladdningsfel stoppar inte Q. */
export async function lookThrough(track: MediaStreamTrack | null | undefined): Promise<QLook | null> {
  if (!track || track.readyState !== 'live') return null;
  const blob = await frameFromTrack(track);
  const look = await blobToLook(blob);
  try {
    const up = await api.uploadFile(new File([blob], `q-titta-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    look.url = up.url;
  } catch { /* bilden går ändå till Q */ }
  return look;
}
